import { profileLocal } from './profiles/local';

/** Order of pinned canvases on "My canvases". First id was pinned first and stays first. */
const PINS_KEY = 'vp-canvas-pins';

export function readPins(): string[] {
  try {
    const raw = profileLocal.getItem(PINS_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const x of v) {
      if (typeof x !== 'string' || seen.has(x)) continue;
      seen.add(x);
      out.push(x);
    }
    return out;
  } catch {
    return [];
  }
}

export function writePins(ids: readonly string[]): void {
  try {
    if (!ids.length) profileLocal.removeItem(PINS_KEY);
    else profileLocal.setItem(PINS_KEY, JSON.stringify(ids));
  } catch {
    /* private mode or a full store */
  }
}

/** Drop ids that are no longer canvases. The same array comes back when nothing changed. */
export function prunePins(ids: readonly string[], existing: ReadonlySet<string>): string[] {
  const next = ids.filter((id) => existing.has(id));
  return next.length === ids.length ? (ids as string[]) : next;
}

/** Append canvases that are not pinned yet, in the order given. */
export function pinCanvases(ids: readonly string[], add: readonly string[]): string[] {
  const have = new Set(ids);
  const next = [...ids];
  for (const id of add) {
    if (have.has(id)) continue;
    have.add(id);
    next.push(id);
  }
  return next;
}

export function unpinCanvases(ids: readonly string[], remove: readonly string[]): string[] {
  const drop = new Set(remove);
  return ids.filter((id) => !drop.has(id));
}

/** Pinned canvases first, in pin order; the rest by last edit, newest first. */
export function sortCanvases<T extends { id: string; updatedAt: number }>(list: readonly T[], pins: readonly string[]): T[] {
  const rank = new Map(pins.map((id, i) => [id, i]));
  return [...list].sort((a, b) => {
    const pa = rank.get(a.id);
    const pb = rank.get(b.id);
    if (pa !== undefined && pb !== undefined) return pa - pb;
    if (pa !== undefined) return -1;
    if (pb !== undefined) return 1;
    return b.updatedAt - a.updatedAt;
  });
}
